import PropTypes from 'prop-types';
import { roleLabel } from '../dsv-role-options';

/**
 * Group flat DataElementPersona link rows into use case → persona buckets.
 * Each persona bucket collects the roles it is linked under (comma-joinable
 * labels) and the underlying `dataElementPersonaId`s so a Remove maps back to
 * the exact rows to delete. Pure — never mutates the input array or its rows.
 *
 * @param {Array<object>} links serialized DE-link rows
 * @returns {Array<{ useCaseId: string, useCaseName: string, personas: Array<object> }>}
 */
function groupLinks(links) {
	const useCaseMap = new Map();
	for (const link of links || []) {
		const ucKey = link.useCaseId || link.useCaseName || '';
		if (!useCaseMap.has(ucKey)) {
			useCaseMap.set(ucKey, {
				useCaseId: link.useCaseId || '',
				useCaseName: link.useCaseName || link.useCaseId || ucKey,
				personas: new Map(),
			});
		}
		const useCase = useCaseMap.get(ucKey);
		const pKey = link.personaId || link.personaName || '';
		const existing = useCase.personas.get(pKey) || {
			personaId: link.personaId || '',
			personaName: link.personaName || link.personaId || pKey,
			roleLabels: [],
			dataElementPersonaIds: [],
		};
		useCase.personas.set(pKey, {
			...existing,
			roleLabels: [...existing.roleLabels, roleLabel(link.deType)],
			dataElementPersonaIds: link.dataElementPersonaId
				? [...existing.dataElementPersonaIds, link.dataElementPersonaId]
				: existing.dataElementPersonaIds,
		});
	}
	return [...useCaseMap.values()].map((useCase) => ({
		useCaseId: useCase.useCaseId,
		useCaseName: useCase.useCaseName,
		personas: [...useCase.personas.values()],
	}));
}

function LinksGroups({ groups, canEditSchema, onRemove }) {
	return (
		<div className="dsv-de-links__list">
			{groups.map((group) => (
				<div className="dsv-de-links__group" key={group.useCaseId || group.useCaseName}>
					<h3 className="dsv-de-links__usecase">{group.useCaseName}</h3>
					<ul className="dsv-de-links__personas">
						{group.personas.map((persona) => (
							<li
								className="dsv-de-links__persona"
								key={persona.personaId || persona.personaName}
								data-testid={`de-link-row-${persona.personaId || persona.personaName}`}
							>
								<div className="dsv-de-links__persona-info">
									<span className="dsv-de-links__persona-name">{persona.personaName}</span>
									<span className="dsv-de-links__persona-roles">
										{persona.roleLabels.join(', ')}
									</span>
								</div>
								{canEditSchema && (
									<button
										type="button"
										className="dsv-btn dsv-btn--retire dsv-btn--sm"
										data-testid={`unlink-btn-${persona.personaId || persona.personaName}`}
										onClick={() => onRemove({
											useCaseId: group.useCaseId,
											useCaseName: group.useCaseName,
											personaId: persona.personaId,
											personaName: persona.personaName,
											dataElementPersonaIds: persona.dataElementPersonaIds,
										})}
									>
										Remove
									</button>
								)}
							</li>
						))}
					</ul>
				</div>
			))}
		</div>
	);
}

LinksGroups.propTypes = {
	groups: PropTypes.arrayOf(PropTypes.object).isRequired,
	canEditSchema: PropTypes.bool,
	onRemove: PropTypes.func,
};

/**
 * Use cases / personas / roles a data element is linked to, with a per-persona
 * Remove control (admins only). Removal is delegated to the parent via
 * `onRemove(personaGroup)` so the confirm + unlink hook + refetch flow lives
 * on the page.
 *
 * Panel variant (DSV detail page): bare list content (or an empty message) for
 * the shared toggle panel — the page owns the "View linked use cases (N)"
 * toggle. Card variant (default) renders the full titled section, hidden
 * entirely when there are no links.
 */
function DeLinksSection({ links, canEditSchema, onRemove, panel }) {
	const groups = groupLinks(links);

	if (panel) {
		return (
			<div className="dsv-de-links dsv-de-links--panel" data-testid="de-links-section">
				{groups.length === 0 ? (
					<p className="dsv-de-links__empty">No use cases linked.</p>
				) : (
					<LinksGroups groups={groups} canEditSchema={canEditSchema} onRemove={onRemove} />
				)}
			</div>
		);
	}

	if (groups.length === 0) return null;
	return (
		<div className="dsv-de-links" data-testid="de-links-section">
			<div className="dsv-de-links__header">
				<h2 className="dsv-de-links__title">Linked Use Cases</h2>
				<p className="dsv-de-links__subtitle">
					Use cases and personas this data element is linked to.
				</p>
			</div>
			<LinksGroups groups={groups} canEditSchema={canEditSchema} onRemove={onRemove} />
		</div>
	);
}

DeLinksSection.propTypes = {
	links: PropTypes.arrayOf(PropTypes.object),
	canEditSchema: PropTypes.bool,
	onRemove: PropTypes.func,
	panel: PropTypes.bool,
};

DeLinksSection.defaultProps = {
	panel: false,
};

export default DeLinksSection;
export { groupLinks };
